import React, { useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { formatIDR, formatDateID } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Search, Plus, Pencil, Trash2, Loader2, Printer, ChevronLeft, ChevronRight, Wallet } from "lucide-react";
import { toast } from "sonner";
import ReceiptDialog from "@/components/ReceiptDialog";

function emptyForm() {
  return { santri_id: "", kitab_id: "", tanggal_bayar: new Date().toISOString().slice(0, 10), status: "lunas", keterangan: "" };
}

export default function PembayaranPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [kelasFilter, setKelasFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const [kelasList, setKelasList] = useState([]);
  const [santriList, setSantriList] = useState([]); // for select

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Auto-loaded kitab list by santri's kelas
  const [kitabOptions, setKitabOptions] = useState([]);
  const [kitabLoading, setKitabLoading] = useState(false);

  const [receiptOpen, setReceiptOpen] = useState(false);
  const [receiptData, setReceiptData] = useState(null);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (q) params.q = q;
      if (statusFilter !== "all") params.status = statusFilter;
      if (kelasFilter !== "all") params.kelas_id = kelasFilter;
      const { data } = await api.get("/pembayaran", { params });
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [page, limit, q, statusFilter, kelasFilter]);

  const loadKelas = useCallback(async () => {
    try {
      const { data } = await api.get("/kelas", { params: { limit: 200 } });
      setKelasList(data.items || []);
    } catch (_e) { /* ignore */ }
  }, []);

  const loadSantri = useCallback(async () => {
    try {
      const { data } = await api.get("/santri", { params: { limit: 500, status: "aktif" } });
      setSantriList(data.items || []);
    } catch (_e) { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadKelas(); loadSantri(); }, [loadKelas, loadSantri]);

  const santriById = useMemo(() => {
    const m = {};
    santriList.forEach((s) => { m[s.id] = s; });
    return m;
  }, [santriList]);

  const fetchKitabForSantri = useCallback(async (santriId) => {
    setKitabOptions([]);
    if (!santriId) return;
    const santri = santriById[santriId];
    if (!santri || !santri.kelas_id) {
      toast.error("Santri belum punya kelas.");
      return;
    }
    setKitabLoading(true);
    try {
      const { data } = await api.get(`/kitab/by-kelas/${santri.kelas_id}`);
      setKitabOptions(data.items || []);
      if ((data.items || []).length === 0) {
        toast.info("Belum ada kitab aktif untuk kelas santri ini.");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setKitabLoading(false);
    }
  }, [santriById]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setKitabOptions([]);
    setDialogOpen(true);
  };
  const openEdit = (it) => {
    setEditing(it);
    setForm({
      santri_id: it.santri_id, kitab_id: it.kitab_id,
      tanggal_bayar: it.tanggal_bayar?.slice(0, 10) || new Date().toISOString().slice(0, 10),
      status: it.status, keterangan: it.keterangan || "",
    });
    fetchKitabForSantri(it.santri_id);
    setDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.santri_id) { toast.error("Santri wajib dipilih"); return; }
    if (!form.kitab_id) { toast.error("Kitab wajib dipilih"); return; }
    setSaving(true);
    try {
      if (editing) {
        // Only allow editing tanggal/status/keterangan (santri/kitab fixed)
        await api.patch(`/pembayaran/${editing.id}`, {
          tanggal_bayar: form.tanggal_bayar,
          status: form.status,
          keterangan: form.keterangan || null,
        });
        toast.success("Pembayaran diperbarui");
      } else {
        await api.post("/pembayaran", {
          santri_id: form.santri_id, kitab_id: form.kitab_id,
          tanggal_bayar: form.tanggal_bayar, status: form.status,
          keterangan: form.keterangan || null,
        });
        toast.success("Pembayaran dicatat");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/pembayaran/${deleteTarget.id}`);
      toast.success("Pembayaran dihapus");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const openReceipt = (p) => { setReceiptData(p); setReceiptOpen(true); };

  // Auto-fill harga from selected kitab
  const selectedKitab = kitabOptions.find((k) => k.id === form.kitab_id);

  return (
    <div className="space-y-6" data-testid="pembayaran-page">
      <div className="flex flex-col gap-1">
        <div className="text-xs font-semibold text-emerald-700 uppercase tracking-widest">Pembayaran</div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">Pembayaran Kitab</h1>
        <p className="text-slate-500 text-sm">Catat pembayaran kitab dan cetak bukti pembayaran.</p>
      </div>

      <Card className="p-4 md:p-5 border border-slate-200 shadow-sm bg-white">
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <div className="relative flex-1">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input placeholder="Cari nama santri / NIS..." value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }}
                   className="pl-9 h-10" data-testid="pembayaran-search-input" />
          </div>
          <Select value={kelasFilter} onValueChange={(v) => { setKelasFilter(v); setPage(1); }}>
            <SelectTrigger className="w-full md:w-52 h-10" data-testid="pembayaran-kelas-filter">
              <SelectValue placeholder="Kelas" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Kelas</SelectItem>
              {kelasList.map((k) => <SelectItem key={k.id} value={k.id}>{k.nama}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
            <SelectTrigger className="w-full md:w-40 h-10" data-testid="pembayaran-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Status</SelectItem>
              <SelectItem value="lunas">Lunas</SelectItem>
              <SelectItem value="belum_lunas">Belum Lunas</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={openCreate} className="bg-emerald-600 hover:bg-emerald-700 text-white h-10 gap-2"
                  data-testid="add-pembayaran-button">
            <Plus className="h-4 w-4" /> Catat Pembayaran
          </Button>
        </div>
      </Card>

      <Card className="border border-slate-200 shadow-sm bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">No. Pembayaran</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Santri</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Kelas</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Kitab</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Harga</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Tanggal</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Status</TableHead>
                <TableHead className="text-right text-xs uppercase tracking-wider text-slate-500 font-semibold">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} className="text-center py-12 text-slate-400">
                  <Loader2 className="h-5 w-5 mx-auto animate-spin" />
                </TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-12 text-slate-400">
                  Belum ada pembayaran.
                </TableCell></TableRow>
              ) : items.map((it) => (
                <TableRow key={it.id} data-testid={`pembayaran-row-${it.id}`} className="hover:bg-emerald-50/40">
                  <TableCell className="font-mono text-xs text-slate-700">{it.nomor}</TableCell>
                  <TableCell>
                    <div className="font-semibold text-slate-900">{it.santri?.nama}</div>
                    <div className="text-xs text-slate-500 font-mono">{it.santri?.nis}</div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-700">{it.kelas?.nama}</TableCell>
                  <TableCell className="text-sm text-slate-700">{it.kitab?.nama}</TableCell>
                  <TableCell className="text-sm font-semibold text-emerald-700 tabular-nums">{formatIDR(it.harga_snapshot)}</TableCell>
                  <TableCell className="text-sm text-slate-700">{formatDateID(it.tanggal_bayar)}</TableCell>
                  <TableCell>
                    <span className={`px-2.5 py-0.5 inline-flex text-xs font-semibold rounded-full ${
                      it.status === "lunas" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"
                    }`}>
                      {it.status === "lunas" ? "Lunas" : "Belum Lunas"}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => openReceipt(it)} data-testid={`receipt-pembayaran-${it.id}`} title="Cetak bukti">
                      <Printer className="h-4 w-4 text-emerald-600" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(it)} data-testid={`edit-pembayaran-${it.id}`}>
                      <Pencil className="h-4 w-4 text-slate-600" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(it)} data-testid={`delete-pembayaran-${it.id}`}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-sm text-slate-600">
          <div>Total: <span className="font-semibold">{total}</span></div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="pembayaran-prev-page">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span data-testid="pembayaran-page-indicator">Hal {page} / {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="pembayaran-next-page">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>

      {/* Form Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Pembayaran" : "Catat Pembayaran Kitab"}</DialogTitle>
            <DialogDescription>
              {editing
                ? "Anda hanya dapat mengubah tanggal, status, dan keterangan."
                : "Pilih santri terlebih dahulu — daftar kitab akan otomatis muncul sesuai kelas santri."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <Label>Santri *</Label>
              <Select
                value={form.santri_id}
                disabled={!!editing}
                onValueChange={(v) => {
                  setForm((f) => ({ ...f, santri_id: v, kitab_id: "" }));
                  fetchKitabForSantri(v);
                }}
              >
                <SelectTrigger data-testid="pembayaran-form-santri">
                  <SelectValue placeholder="Pilih santri" />
                </SelectTrigger>
                <SelectContent>
                  {santriList.length === 0 ? (
                    <div className="px-2 py-3 text-xs text-slate-500">Belum ada santri aktif.</div>
                  ) : santriList.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.nama} • {s.nis}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.santri_id && santriById[form.santri_id] && (
                <p className="text-xs text-slate-500 mt-1">
                  Kelas: <span className="font-semibold text-slate-700">
                    {kelasList.find(k => k.id === santriById[form.santri_id].kelas_id)?.nama || "-"}
                  </span>
                </p>
              )}
            </div>

            <div>
              <Label>Kitab *</Label>
              <Select
                value={form.kitab_id}
                disabled={!!editing || kitabLoading || kitabOptions.length === 0}
                onValueChange={(v) => setForm((f) => ({ ...f, kitab_id: v }))}
              >
                <SelectTrigger data-testid="pembayaran-form-kitab">
                  <SelectValue placeholder={kitabLoading ? "Memuat kitab..." : (kitabOptions.length === 0 ? "Pilih santri terlebih dahulu" : "Pilih kitab")} />
                </SelectTrigger>
                <SelectContent>
                  {kitabOptions.map((k) => (
                    <SelectItem key={k.id} value={k.id}>
                      {k.nama} — {formatIDR(k.harga)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedKitab && (
                <div className="mt-2 p-3 rounded-lg bg-emerald-50/50 border border-emerald-100 flex items-center gap-2">
                  <Wallet className="h-4 w-4 text-emerald-700" />
                  <span className="text-sm text-slate-700">Harga Kitab:</span>
                  <span className="ml-auto font-bold text-emerald-700 tabular-nums">{formatIDR(selectedKitab.harga)}</span>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Tanggal Bayar *</Label>
                <Input required type="date" value={form.tanggal_bayar}
                       onChange={(e) => setForm(f => ({ ...f, tanggal_bayar: e.target.value }))}
                       data-testid="pembayaran-form-tanggal" />
              </div>
              <div>
                <Label>Status *</Label>
                <Select value={form.status} onValueChange={(v) => setForm(f => ({ ...f, status: v }))}>
                  <SelectTrigger data-testid="pembayaran-form-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="lunas">Lunas</SelectItem>
                    <SelectItem value="belum_lunas">Belum Lunas</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Keterangan</Label>
              <Textarea rows={2} value={form.keterangan}
                        onChange={(e) => setForm(f => ({ ...f, keterangan: e.target.value }))}
                        data-testid="pembayaran-form-keterangan" placeholder="Catatan tambahan (opsional)" />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Batal</Button>
              <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="pembayaran-form-submit">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus Pembayaran?</AlertDialogTitle>
            <AlertDialogDescription>
              Pembayaran <span className="font-semibold">{deleteTarget?.nomor}</span> akan dihapus permanen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} data-testid="pembayaran-delete-confirm" className="bg-red-600 hover:bg-red-700">
              Hapus
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ReceiptDialog open={receiptOpen} onOpenChange={setReceiptOpen} pembayaran={receiptData} />
    </div>
  );
}
